import { Box, Divider, Flex, Text, useDisclosure, useToast } from '@chakra-ui/react';
import Big from 'big.js';
import { Form, Formik } from 'formik';
import { useState } from 'react';

import { LEAccordionItem } from '../../components/LEAccordion';
import { useLEAccordionItemContext } from '../../components/LEAccordion/LEAccordionItem/useLEAccordionItem';
import { ItemStatusType } from '../../components/LEAccordion/utils';
import LienholderPayoffInstructions from '../../components/LienholderPayoffInstructions';
import Loader from '../../components/Loader';
import DateInput from '../../components/formComponents/DateInput';
import NumberInput from '../../components/formComponents/NumberInput';
import PrimaryButton from '../../components/ui/buttons/PrimaryButton';
import { LDFlags } from '../../constants/flags';
import { isTreatedAsOther } from '../../constants/lienholders';
import { useTemporaryInfoUpdateMutation } from '../../gql/generated/graphql';
import { useFlag, useTempInfo } from '../../hooks';
import { useStep } from '../../hooks/useStep';
import { logger } from '../../services/sentry';
import { ONE_DAY_MS, checkLastDayOfMonth } from '../../utils/dates';
import { sellsViaMarketplace } from '../../utils/tempInfo';
import { createErrorToast } from '../../utils/toast';
import PayoffEstimateModal from './PayoffEstimateModal';
import { PayoffValues, isPayoffEstimated, validationSchema } from './utils';

const Content = ({ initialStatus }: { initialStatus?: ItemStatusType }) => {
  const { updateState, isExpanded } = useLEAccordionItemContext();
  const { info, infoLoading } = useTempInfo({ fetchPolicy: 'network-only', refresh: isExpanded });
  const step = useStep();
  const toast = useToast();
  const {
    isOpen: isEstimatedModalOpen,
    onClose: onEstimatedModalClose,
    onOpen: onEstimatedModalOpen,
  } = useDisclosure();

  const [modalFormValues, setModalFormValues] = useState<PayoffValues | null>(null);

  const showPayoffEstimatedModal = useFlag(LDFlags.SHOW_PAYOFF_ESTIMATED_MODAL);

  const tempInfoData = info?.data;

  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();

  if (infoLoading || !tempInfoData) {
    return <Loader />;
  }

  const initialValues: PayoffValues = {
    totalPayoff:
      new Big(tempInfoData.vehicle_payoff ?? 0)
        .plus(tempInfoData.sales_tax_from_payoff ?? 0)
        .toNumber() ||
      tempInfoData.user_entered_total_payoff ||
      undefined,
    vehiclePayoff: tempInfoData.vehicle_payoff ?? undefined,
    salesTaxFromPayoff: tempInfoData.sales_tax_from_payoff ?? undefined,
    goodThroughDate: tempInfoData?.good_through_date
      ? new Date(tempInfoData?.good_through_date)
      : undefined,
  };

  const handleSubmit = async (values: PayoffValues, goodToGo?: boolean) => {
    updateState({ status: 'loading' });

    try {
      const { salesTaxFromPayoff, goodThroughDate, totalPayoff } = values;

      if (
        !goodToGo &&
        totalPayoff != null &&
        isPayoffEstimated(totalPayoff) &&
        showPayoffEstimatedModal &&
        !tempInfoData.automatically_fetched_payoff
      ) {
        setModalFormValues(values);
        onEstimatedModalOpen();
        updateState({ status: 'warn' });
        return;
      }

      const data = {
        vehicle_payoff: totalPayoff,
        sales_tax_from_payoff: salesTaxFromPayoff,
        good_through_date: goodThroughDate,
        payoff_includes_sales_tax: false,
        user_entered_total_payoff: totalPayoff,
      };

      await updateTemporaryInfo({
        variables: {
          info: {
            id: info?.id,
            data,
          },
        },
      });

      updateState({ status: 'complete' });

      step.moveNext({ wantsToSell: sellsViaMarketplace(tempInfoData) });
    } catch (e) {
      logger.error('Content.tsx', 'Failed getting and saving payoffs', values, e);
      updateState({ status: 'error' });
      toast(createErrorToast({ errorMessage: 'Failed getting and saving payoffs' }));
    }
  };

  return (
    <>
      <Formik<PayoffValues>
        onSubmit={(values) => handleSubmit(values, false)}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ values, errors, isSubmitting }) => (
          <LEAccordionItem
            accordionKey="payoffAmountAndInstructions"
            title="PAYOFF AMOUNT"
            initialStatus={initialStatus}
          >
            <Box as={Form}>
              <Flex direction={{ base: 'column', md: 'row' }} gap={{ md: 2 }}>
                <NumberInput
                  type="tel"
                  _input={{ inputMode: 'decimal' }}
                  _container={{ textAlign: 'left', w: { base: '100%', md: '50%' } }}
                  label="PAYOFF AMOUNT"
                  name="totalPayoff"
                  placeholder="Payoff Amount"
                  isMoney
                  labelProps={{ alignSelf: { base: 'center', md: 'start' }, mx: 0 }}
                />
                <Box mt={{ base: 6, md: 0 }} w={{ base: '100%', md: '50%' }}>
                  <DateInput
                    hideCalendar
                    label="GOOD-THROUGH DATE"
                    name="goodThroughDate"
                    labelProps={{ alignSelf: { base: 'center', md: 'start' }, mx: 0 }}
                    openToDate={
                      checkLastDayOfMonth() ? new Date(Date.now() + ONE_DAY_MS) : undefined
                    }
                  />
                </Box>
              </Flex>
              <Divider color="grayDarkBackground" mt={4} mb={4} zIndex={1} />
              <Text fontSize="16px" fontWeight="semibold" mb={4} textTransform="uppercase">
                How to find your Payoff Amount with{' '}
                {isTreatedAsOther(tempInfoData)
                  ? tempInfoData?.other_lienholder || 'other'
                  : tempInfoData?.lienholder}
                :
              </Text>
              <LienholderPayoffInstructions lienholderSlug={tempInfoData?.lienholder_slug} />

              <Flex flexDir="column" alignItems="center" gap={6} mt="30px">
                <PrimaryButton
                  trackClick="payoff_amount_and_instructions_submit"
                  type="submit"
                  loading={isSubmitting}
                  disabled={
                    (!values.totalPayoff || !values.goodThroughDate) &&
                    (!!errors.totalPayoff || !!errors.goodThroughDate)
                  }
                >
                  NEXT
                </PrimaryButton>
              </Flex>
            </Box>
          </LEAccordionItem>
        )}
      </Formik>
      <PayoffEstimateModal
        payoffAmount={modalFormValues?.totalPayoff ?? 0}
        onConfirm={() => {
          if (modalFormValues) {
            onEstimatedModalClose();
            handleSubmit(modalFormValues, true);
          }
        }}
        onEstimatedModalClose={onEstimatedModalClose}
        isEstimatedModalOpen={isEstimatedModalOpen}
      />
    </>
  );
};

export default Content;
